import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const TemplateSearchBar = ({ 
  searchQuery = '', 
  onSearchChange, 
  onSortChange,
  sortBy = 'name',
  className = '' 
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortMenuRef = useRef(null);

  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)', icon: 'ArrowUpAZ' },
    { value: 'name_desc', label: 'Name (Z-A)', icon: 'ArrowDownZA' },
    { value: 'recent', label: 'Recently Modified', icon: 'Clock' },
    { value: 'usage', label: 'Most Used', icon: 'TrendingUp' },
    { value: 'category', label: 'Category', icon: 'Tag' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortMenuRef?.current && !sortMenuRef?.current?.contains(event?.target)) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCurrentSortOption = () => {
    return sortOptions?.find(option => option?.value === sortBy) || sortOptions?.[0];
  };

  const handleSortSelect = (value) => {
    onSortChange(value);
    setShowSortMenu(false);
  };

  const handleClearSearch = () => {
    onSearchChange('');
  };

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Search Input */}
      <div className="flex-1 relative">
        <Input
          type="search"
          placeholder="Search templates by name, content, or category..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="pl-10 pr-10"
        />
        <Icon 
          name="Search" 
          size={18} 
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClearSearch}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6"
          >
            <Icon name="X" size={14} />
          </Button>
        )}
      </div>
      {/* Sort Dropdown */}
      <div className="relative" ref={sortMenuRef}>
        <Button
          variant="outline"
          onClick={() => setShowSortMenu(!showSortMenu)}
          iconName={getCurrentSortOption()?.icon}
          iconPosition="left"
          className="hidden sm:flex"
        >
          Sort: {getCurrentSortOption()?.label}
          <Icon name="ChevronDown" size={16} className="ml-2" />
        </Button>

        {/* Mobile Sort Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={() => setShowSortMenu(!showSortMenu)}
          className="sm:hidden"
        >
          <Icon name={getCurrentSortOption()?.icon} size={18} />
        </Button>

        {/* Sort Menu */}
        {showSortMenu && (
          <div className="absolute top-full right-0 mt-2 w-56 bg-surface border border-border rounded-lg shadow-modal z-300">
            <div className="py-2">
              {sortOptions?.map((option) => (
                <button
                  key={option?.value}
                  onClick={() => handleSortSelect(option?.value)}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-muted transition-micro ${
                    sortBy === option?.value ? 'bg-muted text-primary' : 'text-text-secondary'
                  }`}
                >
                  <Icon name={option?.icon} size={16} />
                  <span className="font-medium">{option?.label}</span>
                  {sortBy === option?.value && (
                    <Icon name="Check" size={16} className="ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* View Toggle */}
      <div className="hidden lg:flex items-center space-x-1 bg-muted rounded-lg p-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 bg-surface shadow-card"
        >
          <Icon name="Grid3x3" size={16} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
        >
          <Icon name="List" size={16} />
        </Button>
      </div>
    </div>
  );
};

export default TemplateSearchBar;